import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { SelectWithCreate } from '../../components/ui/select-with-create';
import { Checkbox } from '../../components/ui/checkbox';
import HardwareInfo from '../../components/HardwareInfo';
import ServerSqlLinks from '../../components/ServerSqlLinks';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { Search, Plus, Download, ChevronLeft, ChevronRight, Eye, Server, CheckCircle, AlertTriangle, Edit, Trash2, Copy, Shield, HardDrive, Monitor, Cloud, ChevronUp, ChevronDown, ChevronsUpDown, X } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const initialFormData = {
  hostname: '',
  vm_name: '',
  ip: '',
  ip_mgmt: '',
  ip_privada: '',
  description: '',
  cpu: '',
  ram: '',
  marca: '',
  modelo: '',
  nro_serie: '',
  id_tipo_dispositivo: '',
  site: '',
  os: '',
  cloud: '',
  criticity: '',
  farm: '',
  region: '',
  general_monitoring: 0,
  services_monitoring: 0,
  // Operational fields
  restart_window: '',
  pki: 0,
  costo: '',
  observations: '',
};

// Helper to get badge class for tipo
const getTipoBadgeClass = (tipo) => {
  switch (tipo?.toUpperCase()) {
    case 'VIRTUAL': return 'badge-success';
    case 'FISICO': return 'badge-fisico';
    case 'CLOUD': return 'badge-cloud';
    case 'SERVIDOR': return 'badge-default';
    default: return 'badge-warning';
  }
};

const Servers = () => {
  const [servers, setServers] = useState([]);
  const [sqlByServer, setSqlByServer] = useState({});
  const [sqlLookupError, setSqlLookupError] = useState(false);
  const [sqlRetry, setSqlRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'hostname', dir: 'asc' });
  
  // Global search
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchInput, setGlobalSearchInput] = useState('');
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    hostname: '',
    ip: '',
    tipo: '',
    os: '',
    site: '',
  });
  
  // Active stat filter (for visual feedback)
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Form options from API (for create/edit modal - all catalog values)
  const [formOptions, setFormOptions] = useState({ tipos: [], estados: [], sites: [], os: [], clouds: [], criticities: [], restart_windows: [] });
  
  // Filter options from API (for table filters - only values that exist in servers)
  const [filterOptions, setFilterOptions] = useState({ tipos: [], estados: [], sites: [], os: [] });
  
  // Stats from API
  const [stats, setStats] = useState({ total: 0, fisicos: 0, virtuales: 0, cloud: 0, a_relevar: 0 });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const { canCreate, canUpdate, canDelete } = usePermissions('assets', 'servers');
  const { canRead: canReadSql } = usePermissions('assets', 'sql');

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(servers);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/servers/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/servers/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/servers/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchServers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      
      // Global search (min 3 chars)
      if (globalSearch && globalSearch.length >= 3) {
        params.append('search', globalSearch);
      }
      
      // Add column filters
      if (columnFilters.hostname) params.append('hostname_filter', columnFilters.hostname);
      if (columnFilters.ip) params.append('ip_filter', columnFilters.ip);
      if (columnFilters.description) params.append('description_filter', columnFilters.description);
      if (columnFilters.tipo && columnFilters.tipo !== 'all') params.append('tipo', columnFilters.tipo);
      if (columnFilters.os && columnFilters.os !== 'all') params.append('os_filter', columnFilters.os);
      if (columnFilters.site && columnFilters.site !== 'all') params.append('site', columnFilters.site);

      const response = await api.get(`/assets/servers?${params}`);
      setServers(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar servidores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFormOptions(); fetchFilterOptions(); fetchStats(); }, []);
  useEffect(() => { fetchServers(); }, [pagination.page, pagination.limit, sortConfig]);

  useEffect(() => {
    setSqlByServer({});
    setSqlLookupError(false);
    if (!canReadSql || servers.length === 0) return undefined;

    const controller = new AbortController();
    const params = new URLSearchParams();
    servers.forEach(server => params.append('server_ids', String(server.id)));
    api.get(`/assets/sql/by-servers?${params}`, { signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted) setSqlByServer(data.by_server || {});
      })
      .catch(() => {
        if (!controller.signal.aborted) setSqlLookupError(true);
      });
    return () => controller.abort();
  }, [servers, canReadSql, sqlRetry]);
  
  // Debounced global search
  useEffect(() => {
    const t = setTimeout(() => {
      setGlobalSearch(globalSearchInput);
    }, 800);
    return () => clearTimeout(t);
  }, [globalSearchInput]);
  
  // Fetch when global search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchServers();
  }, [globalSearch]);
  
  // Debounced filter effect
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchServers();
    }, 800);
    return () => clearTimeout(t);
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // Stat card click handler - filters the table
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    
    // Reset filters first
    let newFilters = { hostname: '', ip: '', description: '', tipo: '', os: '', site: '' };
    
    switch (filterType) {
      case 'fisicos':
        newFilters.tipo = 'fisico'; // Search by name
        break;
      case 'virtuales':
        newFilters.tipo = 'virtual'; // Search by name
        break;
      case 'cloud':
        newFilters.tipo = 'cloud'; // Search by name
        break;
      case 'a_relevar':
        newFilters.tipo = 'otros'; // Special value to filter NOT IN known types
        break;
      default:
        // 'all' - keep filters empty
        break;
    }
    
    setColumnFilters(newFilters);
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setCopyingFrom(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = async (server) => {
    try {
      const response = await api.get(`/assets/servers/${server.hostname}`);
      const srv = response.data.server;
      setEditingItem(srv);
      setCopyingFrom(null);
      setFormData({
        hostname: srv.hostname || '',
        vm_name: srv.vm_name || '',
        ip: srv.ip || '',
        ip_mgmt: srv.ip_mgmt || '',
        ip_privada: srv.ip_privada || '',
        description: srv.description || '',
        cpu: srv.cpu || '',
        ram: srv.ram || '',
        marca: srv.marca || '',
        modelo: srv.modelo || '',
        nro_serie: srv.nro_serie || '',
        id_tipo_dispositivo: srv.id_tipo_dispositivo ? String(srv.id_tipo_dispositivo) : '',
        site: srv.site ? String(srv.site) : '',
        os: srv.os ? String(srv.os) : '',
        cloud: srv.cloud ? String(srv.cloud) : '',
        criticity: srv.criticity ? String(srv.criticity) : '',
        farm: srv.farm || '',
        region: srv.region || '',
        general_monitoring: srv.general_monitoring || 0,
        services_monitoring: srv.services_monitoring || 0,
        // Operational fields
        restart_window: srv.restart_window ? String(srv.restart_window) : '',
        pki: srv.pki || 0,
        costo: srv.costo || '',
        observations: srv.observations || '',
      });
      setModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar datos del servidor');
    }
  };

  const handleCopy = async (server) => {
    try {
      const response = await api.get(`/assets/servers/${server.hostname}`);
      const srv = response.data.server;
      setCopyingFrom(srv.hostname);
      setEditingItem(null);
      
      // Unique fields are EMPTY, copied fields are pre-filled
      setFormData({
        // UNIQUE - must be empty
        hostname: '',
        vm_name: '',
        ip: '',
        ip_mgmt: '',
        ip_privada: '',
        nro_serie: '',
        
        // COPIED - pre-filled from original
        description: srv.description || '',
        cpu: srv.cpu || '',
        ram: srv.ram || '',
        marca: srv.marca || '',
        modelo: srv.modelo || '',
        id_tipo_dispositivo: srv.id_tipo_dispositivo ? String(srv.id_tipo_dispositivo) : '',
        estado: srv.estado ? String(srv.estado) : '',
        site: srv.site ? String(srv.site) : '',
        os: srv.os ? String(srv.os) : '',
        cloud: srv.cloud ? String(srv.cloud) : '',
        criticity: srv.criticity ? String(srv.criticity) : '',
        farm: srv.farm || '',
        region: srv.region || '',
        general_monitoring: srv.general_monitoring || 0,
        services_monitoring: srv.services_monitoring || 0,
        // Operational fields - copy these
        restart_window: srv.restart_window ? String(srv.restart_window) : '',
        pki: srv.pki || 0,
        costo: '', // Don't copy costo
        observations: '',
      });
      setCopyModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar datos del servidor');
    }
  };

  const handleSave = async () => {
    if (!formData.hostname.trim()) {
      toast.error('El hostname es requerido');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        cpu: formData.cpu ? parseInt(formData.cpu) : null,
        ram: formData.ram ? parseInt(formData.ram) : null,
        id_tipo_dispositivo: formData.id_tipo_dispositivo ? parseInt(formData.id_tipo_dispositivo) : null,
        site: formData.site ? parseInt(formData.site) : null,
        os: formData.os ? parseInt(formData.os) : null,
        cloud: formData.cloud ? parseInt(formData.cloud) : null,
        criticity: formData.criticity ? parseInt(formData.criticity) : null,
        general_monitoring: formData.general_monitoring ? 1 : 0,
        services_monitoring: formData.services_monitoring ? 1 : 0,
        // New fields
        restart_window: formData.restart_window ? parseInt(formData.restart_window) : null,
        pki: formData.pki ? 1 : 0,
      };

      // El estado se conserva al copiar, pero no se envía al editar otro campo.
      if (copyingFrom && formData.estado) payload.estado = parseInt(formData.estado);

      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });

      if (editingItem) {
        await api.put(`/assets/servers/${editingItem.id}`, payload);
        toast.success('Servidor actualizado exitosamente');
      } else {
        await api.post('/assets/servers', payload);
        toast.success(copyingFrom ? 'Servidor copiado exitosamente' : 'Servidor creado exitosamente');
      }
      setModalOpen(false);
      setCopyModalOpen(false);
      fetchServers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al guardar';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (server) => {
    try {
      await api.delete(`/assets/servers/${server.id}`);
      toast.success('Servidor eliminado exitosamente');
      setDeleteConfirm(null);
      fetchServers();
    } catch (error) {
      toast.error('Error al eliminar servidor');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Renderizar campos del formulario (función, no componente, para evitar re-mount)
  const renderFormFields = (showUniqueSection = false) => (
    <div className="space-y-6">
      {/* Unique Fields Section (only for copy modal) */}
      {showUniqueSection && (
        <div className="p-4 border border-warning/30 rounded-lg bg-warning/5">
          <h4 className="text-sm font-semibold mb-3 text-warning flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Campos únicos (obligatorio completar)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Hostname *</Label>
              <Input 
                value={formData.hostname} 
                onChange={(e) => handleInputChange('hostname', e.target.value)} 
                className="input-subtle mt-1.5"
                data-validation={!formData.hostname ? 'required-empty' : ''}
                placeholder="srv-nuevo-01"
                data-testid="input-hostname-copy" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">VM Name</Label>
              <Input 
                value={formData.vm_name} 
                onChange={(e) => handleInputChange('vm_name', e.target.value)} 
                className="input-subtle mt-1.5"
                data-validation={!formData.vm_name ? 'required-empty' : ''}
                placeholder="vm-nuevo-01" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IP Principal</Label>
              <Input 
                value={formData.ip} 
                onChange={(e) => handleInputChange('ip', e.target.value)} 
                className="input-subtle mt-1.5 font-mono"
                data-validation={!formData.ip ? 'required-empty' : ''}
                placeholder="10.0.0.X" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IP Gestión</Label>
              <Input 
                value={formData.ip_mgmt} 
                onChange={(e) => handleInputChange('ip_mgmt', e.target.value)} 
                className="input-subtle mt-1.5 font-mono"
                placeholder="10.0.1.X" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">IP Privada</Label>
              <Input 
                value={formData.ip_privada} 
                onChange={(e) => handleInputChange('ip_privada', e.target.value)} 
                className="input-subtle mt-1.5 font-mono"
                placeholder="192.168.0.X" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Nro. Serie</Label>
              <Input 
                value={formData.nro_serie} 
                onChange={(e) => handleInputChange('nro_serie', e.target.value)} 
                className="input-subtle mt-1.5 font-mono"
                data-validation={!formData.nro_serie ? 'required-empty' : ''}
                placeholder="NUEVO-SERIAL" 
              />
            </div>
          </div>
        </div>
      )}

      {/* Basic Info (for create/edit) or Copied Fields (for copy modal) */}
      <div className={showUniqueSection ? "p-4 border border-success/30 rounded-lg bg-success/5" : ""}>
        {showUniqueSection && (
          <h4 className="text-sm font-semibold mb-3 text-success flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Campos copiados (puede modificar)
          </h4>
        )}
        
        {!showUniqueSection && (
          <>
            <h4 className="text-sm font-semibold mb-3 text-primary">Información Básica</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label className="text-sm font-medium">Hostname *</Label>
                <Input 
                  value={formData.hostname} 
                  onChange={(e) => handleInputChange('hostname', e.target.value)} 
                  className="input-subtle mt-1.5"
                  data-validation={!formData.hostname ? 'required-empty' : 'valid'}
                  placeholder="srv-app-01"
                  data-testid="input-hostname" 
                />
              </div>
              <div>
                <Label className="text-sm font-medium">VM Name</Label>
                <Input 
                  value={formData.vm_name} 
                  onChange={(e) => handleInputChange('vm_name', e.target.value)} 
                  className="input-subtle mt-1.5"
                  placeholder="vm-app-01" 
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Descripción</Label>
                <Input 
                  value={formData.description} 
                  onChange={(e) => handleInputChange('description', e.target.value)} 
                  className="input-subtle mt-1.5"
                  placeholder="Servidor de aplicaciones principal" 
                />
              </div>
            </div>

            <h4 className="text-sm font-semibold mb-3 text-primary">Red</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label className="text-sm font-medium">IP Principal</Label>
                <Input 
                  value={formData.ip} 
                  onChange={(e) => handleInputChange('ip', e.target.value)} 
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="10.0.0.1" 
                />
              </div>
              <div>
                <Label className="text-sm font-medium">IP Gestión</Label>
                <Input 
                  value={formData.ip_mgmt} 
                  onChange={(e) => handleInputChange('ip_mgmt', e.target.value)} 
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="10.0.1.1" 
                />
              </div>
              <div>
                <Label className="text-sm font-medium">IP Privada</Label>
                <Input 
                  value={formData.ip_privada} 
                  onChange={(e) => handleInputChange('ip_privada', e.target.value)} 
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="192.168.0.1" 
                />
              </div>
            </div>
          </>
        )}

        {/* Hardware */}
        <div className={!showUniqueSection ? "" : "mt-4"}>
          {!showUniqueSection && <h4 className="text-sm font-semibold mb-3 text-primary">Hardware</h4>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm font-medium">CPU (cores)</Label>
              <Input 
                type="number"
                value={formData.cpu} 
                onChange={(e) => handleInputChange('cpu', e.target.value)} 
                className="input-subtle mt-1.5"
                placeholder="4" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">RAM (GB)</Label>
              <Input 
                type="number"
                value={formData.ram} 
                onChange={(e) => handleInputChange('ram', e.target.value)} 
                className="input-subtle mt-1.5"
                placeholder="16" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Marca</Label>
              <Input 
                value={formData.marca} 
                onChange={(e) => handleInputChange('marca', e.target.value)} 
                className="input-subtle mt-1.5"
                placeholder="Dell" 
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Modelo</Label>
              <Input 
                value={formData.modelo} 
                onChange={(e) => handleInputChange('modelo', e.target.value)} 
                className="input-subtle mt-1.5"
                placeholder="PowerEdge R740" 
              />
            </div>
            {!showUniqueSection && (
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Nro. Serie</Label>
                <Input 
                  value={formData.nro_serie} 
                  onChange={(e) => handleInputChange('nro_serie', e.target.value)} 
                  className="input-subtle mt-1.5 font-mono"
                  placeholder="ABC123XYZ" 
                />
              </div>
            )}
          </div>
        </div>

        {/* Classification */}
        <div className="mt-6">
          {!showUniqueSection && <h4 className="text-sm font-semibold mb-3 text-primary">Clasificación</h4>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium">Tipo *</Label>
              <Select value={formData.id_tipo_dispositivo || "none"} onValueChange={(v) => handleInputChange('id_tipo_dispositivo', v === "none" ? "" : v)}>
                <SelectTrigger 
                  className="input-subtle mt-1.5"
                  data-validation={formData.id_tipo_dispositivo && formData.id_tipo_dispositivo !== "none" ? 'valid' : ''}
                >
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin seleccionar</SelectItem>
                  {formOptions.tipos?.map(t => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Site</Label>
              <SelectWithCreate
                value={formData.site}
                onValueChange={(v) => handleInputChange('site', v)}
                options={formOptions.sites || []}
                placeholder="Seleccionar..."
                createEndpoint="/assets/servers/quick-create/site"
                entityName="Site"
                createFields={{
                  name: { label: "Nombre", placeholder: "Ej: TZ_NUEVO" },
                  description: { label: "Descripción", placeholder: "Descripción del site (opcional)" }
                }}
                onCreated={(newItem) => {
                  fetchFormOptions();
                  handleInputChange('site', String(newItem.id));
                }}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Sistema Operativo</Label>
              <Select value={formData.os || "none"} onValueChange={(v) => handleInputChange('os', v === "none" ? "" : v)}>
                <SelectTrigger 
                  className="input-subtle mt-1.5"
                  data-validation={formData.os && formData.os !== "none" ? 'valid' : ''}
                >
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin seleccionar</SelectItem>
                  {formOptions.os?.map(o => (
                    <SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Cloud</Label>
              <Select value={formData.cloud || "none"} onValueChange={(v) => handleInputChange('cloud', v === "none" ? "" : v)}>
                <SelectTrigger 
                  className="input-subtle mt-1.5"
                  data-validation={formData.cloud && formData.cloud !== "none" ? 'valid' : ''}
                >
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin seleccionar</SelectItem>
                  {formOptions.clouds?.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Criticidad</Label>
              <SelectWithCreate
                value={formData.criticity}
                onValueChange={(v) => handleInputChange('criticity', v)}
                options={formOptions.criticities || []}
                placeholder="Seleccionar..."
                createEndpoint="/assets/servers/quick-create/criticidad"
                entityName="Criticidad"
                createFields={{
                  name: { label: "Tipo", placeholder: "Ej: CRITICO" },
                  description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                }}
                onCreated={(newItem) => {
                  fetchFormOptions();
                  handleInputChange('criticity', String(newItem.id));
                }}
                className="mt-1.5"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        {!showUniqueSection && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold mb-3 text-primary">Ubicación</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Farm</Label>
                <Input 
                  value={formData.farm} 
                  onChange={(e) => handleInputChange('farm', e.target.value)} 
                  className="input-subtle mt-1.5"
                  placeholder="Farm-01" 
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Región</Label>
                <Input 
                  value={formData.region} 
                  onChange={(e) => handleInputChange('region', e.target.value)} 
                  className="input-subtle mt-1.5"
                  placeholder="us-east-1" 
                />
              </div>
            </div>
          </div>
        )}

        {/* Operation Section - New */}
        <div className="mt-6">
          {!showUniqueSection && <h4 className="text-sm font-semibold mb-3 text-primary">Operación</h4>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium">Ventana de Reinicio</Label>
              <SelectWithCreate
                value={formData.restart_window}
                onValueChange={(v) => handleInputChange('restart_window', v)}
                options={formOptions.restart_windows || []}
                placeholder="Seleccionar..."
                createEndpoint="/assets/servers/quick-create/ventana-reinicio"
                entityName="Ventana de Reinicio"
                createFields={{
                  name: { label: "Ventana", placeholder: "Ej: Lunes 22:00 - 06:00" },
                  description: { label: "Descripción", placeholder: "Descripción (opcional)" }
                }}
                onCreated={(newItem) => {
                  fetchFormOptions();
                  handleInputChange('restart_window', String(newItem.id));
                }}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Costo</Label>
              <Input 
                value={formData.costo} 
                onChange={(e) => handleInputChange('costo', e.target.value)} 
                className="input-subtle mt-1.5"
                placeholder="$1500" 
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={Boolean(formData.pki)}
                onCheckedChange={(v) => handleInputChange('pki', v ? 1 : 0)}
              />
              <Label className="text-sm font-medium">KPI</Label>
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-sm font-medium">Observaciones</Label>
            <Textarea 
              value={formData.observations} 
              onChange={(e) => handleInputChange('observations', e.target.value)} 
              className="input-subtle mt-1.5"
              placeholder="Notas adicionales..."
              rows={2}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Servidores</h1>
            <p className="page-subtitle">Gestión de servidores e infraestructura</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/servers"
              filters={{
                hostname_filter: columnFilters.hostname,
                ip_filter: columnFilters.ip,
                site: columnFilters.site,
                tipo: columnFilters.tipo,
                os_filter: columnFilters.os,
                search: globalSearch
              }}
              filename="servidores"
            />
            <Button 
              className={`btn btn-primary ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`} 
              onClick={canCreate ? handleCreate : undefined} 
              disabled={!canCreate}
              title={!canCreate ? 'Sin permiso para crear' : undefined}
              data-testid="add-server-btn"
            >
              <Plus className="h-4 w-4" />
              Nuevo Servidor
            </Button>
          </div>
        </div>

        {/* Global Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar en todos los campos (hostname, IP, VM, marca, modelo, serie, tipo, SO, site...)... (mín. 3 caracteres)"
            value={globalSearchInput}
            onChange={(e) => setGlobalSearchInput(e.target.value)}
            className="pl-9 h-10 bg-card border-border"
            data-testid="global-search"
          />
          {globalSearchInput && (
            <button
              onClick={() => { setGlobalSearchInput(''); setGlobalSearch(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Stats - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="servers-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Server className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-blue-500/50 ${activeStatFilter === 'fisicos' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => handleStatFilter('fisicos')}
            data-testid="stats-fisicos"
          >
            <HardDrive className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.fisicos}</div>
              <div className="stats-card-label">Fisicos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-purple-500/50 ${activeStatFilter === 'virtuales' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => handleStatFilter('virtuales')}
            data-testid="stats-virtuales"
          >
            <Monitor className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-purple-500">{stats.virtuales}</div>
              <div className="stats-card-label">Virtuales</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-cyan-500/50 ${activeStatFilter === 'cloud' ? 'ring-2 ring-cyan-500' : ''}`}
            onClick={() => handleStatFilter('cloud')}
            data-testid="stats-cloud"
          >
            <Cloud className="h-6 w-6 text-cyan-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-cyan-500">{stats.cloud}</div>
              <div className="stats-card-label">Cloud</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${activeStatFilter === 'a_relevar' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatFilter('a_relevar')}
            data-testid="stats-relevar"
          >
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.a_relevar}</div>
              <div className="stats-card-label">A Relevar</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {canReadSql && sqlLookupError && (
              <div role="alert" className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm text-muted-foreground">
                No se pudieron cargar los accesos a Motores SQL.
                <button type="button" className="text-primary underline" onClick={() => setSqlRetry(value => value + 1)}>
                  Reintentar
                </button>
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      {/* Headers Row */}
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('hostname')}
                        >
                          <div className="flex items-center gap-1">
                            HOSTNAME
                            <SortIcon field="hostname" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('description')}
                        >
                          <div className="flex items-center gap-1">
                            DESCRIPCIÓN
                            <SortIcon field="description" />
                          </div>
                        </th>
                        <th>TIPO</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip" />
                          </div>
                        </th>
                        <th className="hidden lg:table-cell">OS</th>
                        <th className="hidden xl:table-cell">BUILD</th>
                        <th className="hidden lg:table-cell">SITE</th>
                        <th className="w-28 text-right">ACCIONES</th>
                      </tr>
                      {/* Filters Row */}
                      <tr className="bg-muted/30">
                        <th className="w-10 py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.hostname}
                            onChange={(e) => handleFilterChange('hostname', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-hostname"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.description || ''}
                            onChange={(e) => handleFilterChange('description', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-description"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Select value={columnFilters.tipo || "all"} onValueChange={(v) => handleFilterChange('tipo', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.tipos?.map(t => (
                                <SelectItem key={t.id} value={String(t.id)}>{t.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Filtrar..."
                            value={columnFilters.ip}
                            onChange={(e) => handleFilterChange('ip', e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-ip"
                          />
                        </th>
                        <th className="py-2 px-4 hidden lg:table-cell">
                          <Select value={columnFilters.os || "all"} onValueChange={(v) => handleFilterChange('os', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.os?.map(o => (
                                <SelectItem key={o.id} value={String(o.id)}>{o.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="hidden xl:table-cell py-2 px-4"></th>
                        <th className="py-2 px-4 hidden lg:table-cell">
                          <Select value={columnFilters.site || "all"} onValueChange={(v) => handleFilterChange('site', v)}>
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              {filterOptions.sites?.map(s => (
                                <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {servers.length === 0 ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="empty-state">
                              <Server className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron servidores</p>
                            </div>
                          </td>
                        </tr>
                      ) : servers.map((server) => (
                        <tr key={server.id} data-testid={`server-row-${server.hostname}`} className={isSelected(server.id) ? 'bg-primary/5' : ''}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(server.id)}
                              onCheckedChange={() => toggleRow(server.id)}
                              aria-label={`Seleccionar ${server.hostname}`}
                            />
                          </td>
                          <td className="font-medium text-primary">
                            <div className="flex items-center gap-1.5">
                              {server.hostname}
                              {server.has_hardware ? (
                                <HardwareInfo hostname={server.hostname} updatedAt={server.hardware_last_update} />
                              ) : null}
                              {canReadSql && <ServerSqlLinks hostname={server.hostname} instances={sqlByServer[String(server.id)]} />}
                            </div>
                          </td>
                          <td className="text-muted-foreground max-w-[200px] truncate" title={server.description || ''}>
                            {server.description || '-'}
                          </td>
                          <td>
                            <span className={`badge ${getTipoBadgeClass(server.tipo_nombre)}`}>
                              {server.tipo_nombre || 'Sin especificar'}
                            </span>
                          </td>
                          <td className="font-mono text-sm text-muted-foreground">{server.ip || '-'}</td>
                          <td className="hidden lg:table-cell text-muted-foreground">{server.os_nombre || '-'}</td>
                          <td className="hidden xl:table-cell font-mono text-xs text-primary">{server.os_build || server.os_build_short || '-'}</td>
                          <td className="hidden lg:table-cell text-muted-foreground">{server.site_nombre || '-'}</td>
                          <td className="w-28 text-right">
                            <div className="flex justify-end gap-0.5">
                              <Link to={`/assets/servers/${server.hostname}`}>
                                <button className="action-btn" title="Ver detalle" data-testid={`view-server-${server.hostname}`}>
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                              </Link>
                              <button 
                                onClick={() => canUpdate && handleEdit(server)} 
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50 cursor-not-allowed' : ''}`} 
                                title={canUpdate ? "Editar" : "Sin permiso para editar"}
                                disabled={!canUpdate}
                                data-testid={`edit-server-${server.hostname}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => canCreate && handleCopy(server)} 
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50 cursor-not-allowed' : ''}`} 
                                title={canCreate ? "Copiar registro" : "Sin permiso para crear"}
                                disabled={!canCreate}
                                data-testid={`copy-server-${server.hostname}`}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                              <button 
                                onClick={() => canDelete && setDeleteConfirm(server)} 
                                className={`action-btn-delete ${!canDelete ? 'opacity-50 cursor-not-allowed' : ''}`} 
                                title={canDelete ? "Eliminar" : "Sin permiso para eliminar"}
                                disabled={!canDelete}
                                data-testid={`delete-server-${server.hostname}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-muted-foreground">
                      {pagination.total > 0 ? `${((pagination.page - 1) * pagination.limit) + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} de ${pagination.total}` : '0 resultados'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pagination.limit.toString()}
                        onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="h-8 w-8" data-testid="prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm px-2">{pagination.page} / {pagination.pages || 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= pagination.pages} className="h-8 w-8" data-testid="next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar Servidor' : 'Nuevo Servidor'}
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              {renderFormFields(false)}
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar Servidor
              </DialogTitle>
              {copyingFrom && (
                <p className="text-sm text-muted-foreground mt-1">
                  Copiando desde: <span className="font-mono text-primary">{copyingFrom}</span>
                </p>
              )}
            </DialogHeader>
            <div className="modal-body py-4">
              {renderFormFields(true)}
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setCopyModalOpen(false)} disabled={saving}>Cancelar</Button>
              <Button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Creando...' : 'Crear Copia'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="modal-content">
            <DialogHeader className="modal-header">
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <div className="modal-body py-4">
              <p className="text-muted-foreground">
                ¿Está seguro que desea eliminar el servidor <span className="font-semibold text-foreground">{deleteConfirm?.hostname}</span>?
              </p>
              <p className="text-sm text-destructive mt-2">
                Esta acción eliminará también todos los registros relacionados y no se puede deshacer.
              </p>
            </div>
            <DialogFooter className="modal-footer">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm)}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar - site, tipo, os */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchServers(); fetchStats(); }}
          endpoint="/assets/servers"
          options={{
            sites: formOptions.sites,
            tipos: formOptions.tipos,
            os: formOptions.os
          }}
          fields={['site_id', 'id_tipo_dispositivo', 'os']}
        />
      </div>
    </Layout>
  );
};

export default Servers;
